import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { accordionAnimation, arrowRotateAnimation } from '@shared-ui';
import {
  ActiveErrorsStore,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  RemoteDiagnosticsStore,
} from '@store';
import { stickyPanel } from 'e2e/constants';

import { UIService } from '@services';
import { ActivatedRoute } from '@angular/router';
import { DiagnosticsOverlayTypeEnum } from '../../installation-overlay.model';
import { Feature, FeatureNames, HeatingBurnersNErrorStatusProperties } from '@vi-iot/client/index';

@Component({
  selector: 'app-active-errors-card',
  templateUrl: './active-errors-card.component.html',
  styleUrls: ['./active-errors-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [arrowRotateAnimation, accordionAnimation],
})
export class ActiveErrorsCardComponent implements OnInit, OnDestroy {
  isExpanded = false;
  limitedListMaxLength = stickyPanel.LIMITED_LIST_MAX_LENGTH;
  installationId: number;
  gatewaySerial: string;
  remoteDiagnosticsActiveSessionDeviceSerial: string;
  burnerErrorStatus$: Observable<Feature<HeatingBurnersNErrorStatusProperties, never>>;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public activeErrorsStore: ActiveErrorsStore,
    public installationDetailsStore: InstallationDetailsStore,
    private uiService: UIService,
    private activatedRoute: ActivatedRoute,
    private remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private cdr: ChangeDetectorRef,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  get expandedState(): 'expanded' | 'collapsed' {
    return this.isExpanded ? 'expanded' : 'collapsed';
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((param) => {
      this.installationId = +param['id'];
    });
    this.activatedRoute.queryParamMap.subscribe((paramMap) => {
      this.gatewaySerial = paramMap.get('gatewaySerial');
    });
    this.remoteDiagnosticsStore
      .getConnectedDeviceSerial(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((deviceSerial) => {
        this.remoteDiagnosticsActiveSessionDeviceSerial = deviceSerial;
        this.cdr.markForCheck();
      });
    this.burnerErrorStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.burnerErrorStatus
    );
  }

  showMoreActiveErrors(): void {
    this.isExpanded = !this.isExpanded;
    this.activeErrorsStore.changeErrorsVisibility();
  }

  navigateToErrorDetails(value: {
    gatewaySerial: string;
    deviceSerial: string;
    activeErrorId: number;
    isLockedSystem?: boolean;
  }): void {
    this.uiService.isOverlayOpen$
      .pipe(take(1), filter(Boolean), takeUntil(this.ngUnsubscribe$))
      .subscribe(() => {
        this.uiService.closeActiveOverlay();
      });

    this.uiService.isSingleRoomControlOverlayOpen$
      .pipe(take(1), filter(Boolean), takeUntil(this.ngUnsubscribe$))
      .subscribe(() => {
        this.uiService.closeActiveOverlay(value.gatewaySerial, this.installationId);
      });

    this.uiService.openDiagnosticOverlay(
      DiagnosticsOverlayTypeEnum.GuidedTroubleshooting,
      this.installationId,
      value.gatewaySerial,
      value.deviceSerial
    );

    this.activatedRoute.queryParams.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((params) => {
      if (params['deviceSerial']) {
        this.activeErrorsStore.selectActiveError(
          value.gatewaySerial,
          value.activeErrorId,
          value.isLockedSystem
        );
      }
    });

    this.uiService.closeExpandedMobileStickyPanel$.next();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
